import React from "react";
import {
  FaArrowDown,
  FaArrowUp,
  FaEdit,
  FaEye,
  FaEyeSlash,
  FaGlobe,
  FaImage,
  FaPhotoVideo,
  FaTrash,
  FaUpload,
} from "react-icons/fa";

import { toAbsolutePhotoUrl } from "../../../../../../../services/partenairesPhotoServices";

import {
  PARTENAIRE_FALLBACK_IMAGE,
  PARTENAIRE_PHOTO_TARGETS,
} from "../utils/partenairesSettingsUtils";

function ActionButton({ title, onClick, disabled, children, tone = "default" }) {
  const tones = {
    default:
      "border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800",
    primary:
      "border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300",
    success:
      "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-300",
    danger:
      "border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300",
  };

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`inline-flex h-9 w-9 items-center justify-center rounded-xl border transition disabled:cursor-not-allowed disabled:opacity-50 ${
        tones[tone] || tones.default
      }`}
    >
      {children}
    </button>
  );
}

function MiniUploadButton({ label, onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className="inline-flex items-center gap-1 rounded-xl border border-slate-200 bg-white px-2.5 py-1.5 text-xs font-bold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
      title={`Changer ${label}`}
    >
      <FaUpload className="text-[11px]" />
      {label}
    </button>
  );
}

function SkeletonRows() {
  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="space-y-3 p-4 sm:p-5 lg:p-6">
        {[...Array(5)].map((_, index) => (
          <div
            key={index}
            className="h-20 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800"
          />
        ))}
      </div>
    </section>
  );
}

function EmptyState() {
  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-10 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-[24px] bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
          <FaImage />
        </div>

        <h3 className="mt-5 text-lg font-bold text-slate-900 dark:text-white">
          Aucun partenaire
        </h3>

        <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
          Ajoutez un partenaire ou modifiez vos filtres.
        </p>
      </div>
    </section>
  );
}

export default function PartenairesTable({
  partenaires,
  loading,
  deletingId,
  togglingId,
  reordering,
  onEdit,
  onDelete,
  onToggleActive,
  onMoveUp,
  onMoveDown,
  onUpload,
}) {
  if (loading) return <SkeletonRows />;
  if (!partenaires.length) return <EmptyState />;

  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-4 sm:p-5 lg:p-6">
        <div className="mb-4">
          <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
            Liste
          </p>

          <h3 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
            Partenaires enregistrés
          </h3>

          <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
            Gérez l’ordre d’affichage, la visibilité, les pages détail et les
            visuels. Le tableau est prévu pour glisser horizontalement sur petits
            écrans.
          </p>
        </div>

        <div className="overflow-x-auto rounded-[22px] border border-slate-200 dark:border-slate-800">
          <table className="w-full min-w-[1280px]">
            <thead className="bg-slate-50 text-xs uppercase tracking-[0.14em] text-slate-500 dark:bg-slate-950/70 dark:text-slate-400">
              <tr>
                <th className="px-4 py-3 text-left font-bold">Ordre</th>
                <th className="px-4 py-3 text-left font-bold">Logo</th>
                <th className="px-4 py-3 text-left font-bold">Partenaire</th>
                <th className="px-4 py-3 text-left font-bold">Lien</th>
                <th className="px-4 py-3 text-left font-bold">Coordonnées</th>
                <th className="px-4 py-3 text-left font-bold">Statut</th>
                <th className="px-4 py-3 text-left font-bold">Page détail</th>
                <th className="px-4 py-3 text-left font-bold">Images</th>
                <th className="sticky right-0 z-20 border-l border-slate-200 bg-slate-50 px-4 py-3 text-right font-bold dark:border-slate-700 dark:bg-slate-950">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
              {partenaires.map((partenaire, index) => {
                const logoUrl =
                  toAbsolutePhotoUrl(partenaire.photo_url) ||
                  PARTENAIRE_FALLBACK_IMAGE;

                return (
                  <tr
                    key={partenaire.id}
                    className="transition hover:bg-slate-50 dark:hover:bg-slate-800/50"
                  >
                    <td className="px-4 py-4 align-top">
                      <div className="flex items-center gap-2">
                        <span className="min-w-8 rounded-xl bg-slate-100 px-2 py-1 text-center text-xs font-bold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                          {partenaire.ordre || index + 1}
                        </span>

                        <div className="flex flex-col">
                          <button
                            type="button"
                            onClick={() => onMoveUp(partenaire)}
                            disabled={index === 0 || reordering}
                            className="rounded-md p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-30 dark:hover:bg-slate-800 dark:hover:text-white"
                            title="Monter"
                          >
                            <FaArrowUp className="text-[11px]" />
                          </button>

                          <button
                            type="button"
                            onClick={() => onMoveDown(partenaire)}
                            disabled={
                              index === partenaires.length - 1 || reordering
                            }
                            className="rounded-md p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-30 dark:hover:bg-slate-800 dark:hover:text-white"
                            title="Descendre"
                          >
                            <FaArrowDown className="text-[11px]" />
                          </button>
                        </div>
                      </div>
                    </td>

                    <td className="px-4 py-4 align-top">
                      <button
                        type="button"
                        onClick={() => onUpload(partenaire, "photo_url")}
                        className="group relative h-16 w-24 overflow-hidden rounded-2xl bg-white ring-1 ring-slate-200 dark:bg-slate-950 dark:ring-slate-700"
                        title="Changer le logo"
                      >
                        <img
                          src={logoUrl}
                          alt={partenaire.nom || "Partenaire"}
                          className="h-full w-full object-contain p-1.5"
                          referrerPolicy="no-referrer"
                          crossOrigin="anonymous"
                          onError={(event) => {
                            event.currentTarget.src =
                              PARTENAIRE_FALLBACK_IMAGE;
                          }}
                        />

                        <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-slate-950/0 text-[10px] font-bold text-white opacity-0 transition group-hover:bg-slate-950/50 group-hover:opacity-100">
                          Changer
                        </span>
                      </button>
                    </td>

                    <td className="px-4 py-4 align-top">
                      <p className="font-bold text-slate-900 dark:text-white">
                        {partenaire.nom || "Sans nom"}
                      </p>

                      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                        ID : {partenaire.id}
                      </p>
                    </td>

                    <td className="max-w-[250px] px-4 py-4 align-top">
                      {partenaire.link_url ? (
                        <a
                          href={partenaire.link_url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-2 text-sm font-semibold text-indigo-700 hover:underline dark:text-indigo-300"
                        >
                          <FaGlobe />
                          <span className="line-clamp-2 break-all">
                            {partenaire.link_url}
                          </span>
                        </a>
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>

                    <td className="max-w-[260px] px-4 py-4 align-top text-slate-700 dark:text-slate-200">
                      <div className="space-y-1 text-xs leading-5">
                        {partenaire.tel ? <p>Tél. : {partenaire.tel}</p> : null}
                        {partenaire.Mail ? (
                          <p>Email : {partenaire.Mail}</p>
                        ) : null}
                        {partenaire.adresse ? (
                          <p className="line-clamp-2">
                            Adresse : {partenaire.adresse}
                          </p>
                        ) : null}

                        {!partenaire.tel &&
                        !partenaire.Mail &&
                        !partenaire.adresse ? (
                          <span className="text-slate-400">—</span>
                        ) : null}
                      </div>
                    </td>

                    <td className="px-4 py-4 align-top">
                      <button
                        type="button"
                        onClick={() => onToggleActive(partenaire)}
                        disabled={togglingId === partenaire.id}
                        className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-bold transition disabled:cursor-not-allowed disabled:opacity-60 ${
                          partenaire.is_active
                            ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20"
                            : "bg-slate-100 text-slate-600 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700"
                        }`}
                      >
                        {partenaire.is_active ? <FaEye /> : <FaEyeSlash />}
                        {partenaire.is_active ? "Affiché" : "Masqué"}
                      </button>
                    </td>

                    <td className="px-4 py-4 align-top">
                      <span
                        className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${
                          partenaire.is_detail
                            ? "bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/20"
                            : "bg-slate-100 text-slate-600 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700"
                        }`}
                      >
                        {partenaire.is_detail ? "Oui" : "Non"}
                      </span>
                    </td>

                    <td className="px-4 py-4 align-top">
                      <div className="flex flex-wrap gap-2">
                        <MiniUploadButton
                          label={PARTENAIRE_PHOTO_TARGETS.photo_url.shortLabel}
                          onClick={() => onUpload(partenaire, "photo_url")}
                        />

                        <MiniUploadButton
                          label={
                            PARTENAIRE_PHOTO_TARGETS.photo2_url.shortLabel
                          }
                          onClick={() => onUpload(partenaire, "photo2_url")}
                        />

                        <MiniUploadButton
                          label={
                            PARTENAIRE_PHOTO_TARGETS.photo3_url.shortLabel
                          }
                          onClick={() => onUpload(partenaire, "photo3_url")}
                        />
                      </div>
                    </td>

                    <td className="sticky right-0 z-10 border-l border-slate-200 bg-white px-4 py-4 align-top dark:border-slate-700 dark:bg-slate-900">
                      <div className="flex items-center justify-end gap-2">
                        <ActionButton
                          title="Modifier"
                          onClick={() => onEdit(partenaire)}
                          tone="primary"
                        >
                          <FaEdit />
                        </ActionButton>

                        <ActionButton
                          title="Supprimer"
                          onClick={() => onDelete(partenaire)}
                          disabled={deletingId === partenaire.id}
                          tone="danger"
                        >
                          <FaTrash />
                        </ActionButton>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <p className="mt-3 flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
          <FaPhotoVideo />
          Les images secondaires sont principalement utiles si la page détail du
          partenaire est activée.
        </p>
      </div>
    </section>
  );
}